import BaseApiService from './BaseApiService'
import type { BillingStatus } from '../types'
import type { CurrentBillResponse, PlanCatalog } from '../types/billingStatement'

export interface SetupIntentResponse {
  clientSecret: string
}

export interface BankSetupIntentResponse {
  clientSecret: string
  setupIntentId: string
}

export interface BankConfirmResponse {
  verified: boolean
  requiresMicrodeposits: boolean
  bankName: string | null
  last4: string | null
}

/**
 * Card management and billing standing.
 *
 * There is no subscribe or cancel: the product bills per document rendered (FR-1.7.2), so there is
 * nothing to subscribe to. A team registers a card and the monthly job charges it for what they used.
 */
class BillingApiService extends BaseApiService {
  /** Starts card collection. The token goes to Stripe.js; no card detail reaches our servers. */
  setupIntent(): Promise<SetupIntentResponse> {
    return this.post<SetupIntentResponse>('/v1/payment/setup-intent')
  }

  /** Records a card the browser has just registered, so the page can render without calling Stripe. */
  attachPaymentMethod(paymentMethodRef: string): Promise<void> {
    return this.post<void>('/v1/payment/payment-method', { paymentMethodRef })
  }

  /** Drops the team back to the free allowance. A balance already owed stays owed. */
  removePaymentMethod(): Promise<void> {
    return this.post<void>('/v1/payment/payment-method/remove')
  }

  /** Starts ACH bank-account collection. Returns the SetupIntent client secret + id for Stripe.js. */
  bankAccountSetupIntent(): Promise<BankSetupIntentResponse> {
    return this.post<BankSetupIntentResponse>('/v1/payment/bank-account/setup-intent')
  }

  /** Records the bank account after Stripe.js confirmed its setup (and the ACH mandate). */
  confirmBankAccount(setupIntentId: string): Promise<BankConfirmResponse> {
    return this.post<BankConfirmResponse>('/v1/payment/bank-account/confirm', { setupIntentId })
  }

  /** Confirms a micro-deposit bank setup with the two amounts (in cents) the customer received. */
  verifyMicrodeposits(setupIntentId: string, amounts: number[]): Promise<{ verified: boolean }> {
    return this.post<{ verified: boolean }>('/v1/payment/bank-account/verify-microdeposits', { setupIntentId, amounts })
  }

  /**
   * Records an ask for a negotiated rate. Every plan is self-served, so this is for a team whose volume
   * has outgrown Ultimate's published per-page price — a conversation rather than a switch.
   */
  requestCustomPricing(): Promise<void> {
    return this.post<void>('/v1/payment/request-custom-pricing')
  }

  getStatus(): Promise<BillingStatus> {
    return this.post<BillingStatus>('/v1/payment/status')
  }

  /** The open period's bill so far, priced as the monthly run will price it, plus past bills with lines. */
  getCurrentBill(): Promise<CurrentBillResponse> {
    return this.post<CurrentBillResponse>('/v1/billing/current-bill')
  }

  /** The self-serve plan comparison: prices from billing_rate, features from PlanFeatures. */
  getPlans(): Promise<PlanCatalog> {
    return this.post<PlanCatalog>('/v1/billing/plans')
  }

  /**
   * The team's overage settings: whether pages past the included allowance are billed (default) or refused, and — while
   * billed — a monthly limit in whole dollars (null = no limit). With overage off the stored limit is kept. Team Admin
   * or Manage billing.
   */
  setOverageSettings(allowOverage: boolean, overageLimitDollars: number | null): Promise<{ allowOverage: boolean; overageLimitDollars: number | null }> {
    return this.post<{ allowOverage: boolean; overageLimitDollars: number | null }>('/v1/payment/overage-settings', { allowOverage, overageLimitDollars })
  }

  /** Self-serve plan change among Standard / Enhanced / Ultimate (Manage-plan capability). */
  setPlan(planCode: string): Promise<{ planCode: string }> {
    return this.post<{ planCode: string }>('/v1/payment/set-plan', { planCode })
  }
}

export const billingApiService = new BillingApiService()
